import { Utils, restoreAttribute } from '../core/utils';
import { Component, BaseOptions, } from '../core/component';

export interface CardsOptions extends BaseOptions {
  onOpen: (el: Element) => void;
  onClose: (el: Element) => void;
}

const _defaults: CardsOptions = {
  onOpen: null,
  onClose: null
};

/**
 * What counts as a card reveal.
 *
 * Shared with `components/registry.ts` so `Cards.Init()` and `AutoInit()` use
 * the same semantic selector.
 */
export const CARDS_SELECTOR =
  'article:has(> aside[id]:not([id=""])):not(:has(> aside ~ aside)):has(> button.card-reveal-trigger[type="button"], > :not(aside) button.card-reveal-trigger[type="button"])';

const REVEAL_INITIALIZED_CLASS = 'card-reveal-initialized';

function hasAccessibleName(trigger: HTMLButtonElement) {
  const label = trigger.getAttribute('aria-label');
  if (label?.trim()) return true;

  const labelledBy = trigger.getAttribute('aria-labelledby');
  if (labelledBy) {
    const referencedText = labelledBy
      .trim()
      .split(/\s+/)
      .map((id) => Utils.getElementById(trigger, id)?.textContent ?? '')
      .join(' ')
      .trim();
    if (referencedText) return true;
  }

  const content = trigger.cloneNode(true) as HTMLButtonElement;
  content.querySelectorAll('[aria-hidden="true"]').forEach((node) => node.remove());
  return Boolean(content.textContent?.trim());
}

function isUsableCardTrigger(
  trigger: HTMLButtonElement,
  card: Element,
  panel: HTMLElement
) {
  return (
    trigger.closest('article') === card &&
    !panel.contains(trigger) &&
    !trigger.disabled &&
    trigger.getAttribute('aria-disabled') !== 'true' &&
    hasAccessibleName(trigger)
  );
}

export function isCardReveal(el: Element): el is HTMLElement {
  if (el.tagName !== 'ARTICLE') return false;
  const panels = el.querySelectorAll<HTMLElement>(':scope > aside');
  if (panels.length !== 1 || !panels[0].id) return false;

  return Array.from(
    el.querySelectorAll<HTMLButtonElement>('button.card-reveal-trigger[type="button"]')
  ).some((trigger) => isUsableCardTrigger(trigger, el, panels[0]));
}

export class Cards extends Component<CardsOptions> {
  isOpen: boolean = false;
  private readonly cardReveal: HTMLElement | null;
  private _activators: HTMLButtonElement[];
  private _ownsRevealInitializedClass = false;
  private _initialAriaControls = new Map<HTMLButtonElement, string | null>();
  private _lastActivator: HTMLButtonElement | null = null;

  constructor(el: HTMLElement, options: Partial<CardsOptions> = {}) {
    super(el, options);

    const cardReveals = this.el.querySelectorAll<HTMLElement>(':scope > aside');
    const cardReveal = cardReveals.length === 1 ? cardReveals[0] : null;
    const activators = cardReveal
      ? Array.from(
          this.el.querySelectorAll<HTMLButtonElement>(
            'button.card-reveal-trigger[type="button"]'
          )
        ).filter(
          (activator) => isUsableCardTrigger(activator, this.el, cardReveal)
        )
      : [];

    this.cardReveal = cardReveal?.id && activators.length > 0 ? cardReveal : null;
    this._activators = this.cardReveal ? activators : [];

    if (this.cardReveal) {
      this._ownsRevealInitializedClass = !this.el.classList.contains(REVEAL_INITIALIZED_CLASS);
      this.el.classList.add(REVEAL_INITIALIZED_CLASS);
      this._activators.forEach((activator) => {
        this._initialAriaControls.set(activator, activator.getAttribute('aria-controls'));
        activator.ariaExpanded = 'false';
        activator.setAttribute('aria-controls', this.cardReveal.id);
      });

      this.cardReveal.classList.remove('open');
      this.cardReveal.inert = true;
      this._setupEventHandlers();
    }
  }

  static get defaults(): CardsOptions {
    return _defaults;
  }

  static key = 'Expressive_Cards';

  /**
   * {@inheritDoc}
   */
  destroy() {
    if (this.cardReveal) {
      this.cardReveal.classList.remove('open');
      this.cardReveal.inert = false;
      if (this._ownsRevealInitializedClass) this.el.classList.remove(REVEAL_INITIALIZED_CLASS);
    }
    this._activators.forEach((activator) => {
      activator.removeAttribute('aria-expanded');
      const initialAriaControls = this._initialAriaControls.get(activator);
      if (initialAriaControls !== undefined) restoreAttribute(activator, 'aria-controls', initialAriaControls);
    });
    this.isOpen = false;
    this._activators = [];
    this._initialAriaControls.clear();
    this._lastActivator = null;
    super.destroy();
  }

  _setupEventHandlers = () => {
    const { signal } = this._events;
    this._activators.forEach((activator) => activator.addEventListener('click', this._handleClickInteraction, { signal }));
    this.el.addEventListener('keydown', this._handleKeydownEvent, { signal });
  };

  _handleClickInteraction = (event: MouseEvent) => {
    this._lastActivator = event.currentTarget as HTMLButtonElement;
    this._handleRevealEvent();
  };

  _handleKeydownEvent = (event: KeyboardEvent) => {
    const target = event.target instanceof Element ? event.target.closest('article') : null;
    if (target !== this.el || event.key !== 'Escape' || !this.isOpen) return;
    event.preventDefault();
    this.close();
  };

  _handleRevealEvent = () => {
    if (this.isOpen) this.close();
    else this.open();
  };

  /**
   * Show card reveal.
   */
  open: () => void = () => {
    if (this.isOpen || !this.cardReveal) return;
    this.isOpen = true;
    this.cardReveal.classList.add('open');
    this.cardReveal.inert = false;
    this._activators.forEach((activator) => (activator.ariaExpanded = 'true'));
    this._notify('open', this.options.onOpen, { current: () => this.isOpen });
  };

  /**
   * Hide card reveal.
   */
  close: () => void = () => {
    if (!this.isOpen || !this.cardReveal) return;

    // In a shadow root document.activeElement is the host, never the reveal.
    const focused = (this.el.getRootNode() as Document | ShadowRoot).activeElement;
    const focusWasInside = this.cardReveal.contains(focused);
    this.isOpen = false;
    this.cardReveal.classList.remove('open');
    this.cardReveal.inert = true;
    this._activators.forEach((activator) => (activator.ariaExpanded = 'false'));
    this._notify('close', this.options.onClose, { current: () => !this.isOpen });

    if (focusWasInside) {
      (this._lastActivator ?? this._activators[0])?.focus();
    }
  };

  static Init() {
    Utils.onDocumentReady(() => {
      const cards = Array.from(document.querySelectorAll(CARDS_SELECTOR)).filter(isCardReveal);
      cards.forEach((el) => {
        if (el['Expressive_Cards'] == undefined) this.init(el);
      });
    });
  }
}
